import { useCallback, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Stack from '@mui/material/Stack';
import { useMediaQuery, useTheme } from '@mui/material';

import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import { useContentEditGate } from '../../hooks/useContentEditGate.hook';
import { isParent } from '../../utils/is-parent.util';
import { pathToChapters } from '../../utils/path-to-chapters.util';
import { nextPath } from '../../utils/next-path.util';
import type { EnrichedSection } from '../../types/enriched-section.type';

import { DropZone } from './DropZone';
import { ItemTypes } from './utils/item-types.util';

interface Props {
  readonly section: EnrichedSection;
  readonly showTarget?: boolean;
  readonly isLastChild?: boolean;
}

export const Section = ({
  section,
  showTarget,
  isLastChild,
}: Readonly<Props>) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));
  const { isAllowed: isContentEditable } = useContentEditGate();

  //----------------------------------------------------------------
  // 1. useDrag hook
  //----------------------------------------------------------------
  const [{ opacity, isDragging }, dragRef, previewRef] = useDrag(
    () => ({
      type: ItemTypes.SECTION,
      item: section,
      canDrag: () => isContentEditable,
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
        isDragging: monitor.isDragging(),
      }),
    }),
    [isContentEditable, section._id, section.realPath],
  );

  //----------------------------------------------------------------
  // 2. useDrop hook
  //----------------------------------------------------------------

  const [{ isOverCurrent, canDrop }, dropRef] = useDrop<
    EnrichedSection,
    unknown,
    Readonly<{ isOverCurrent: boolean; canDrop: boolean }>
  >(
    () => ({
      accept: ItemTypes.SECTION,
      canDrop: (other: Readonly<EnrichedSection>) => {
        // TODO : use pathsAreEqual ?
        const dropIsSame = section._id === other._id;
        const dropIsParent = isParent(other.realPath, section.realPath);
        const canDrop = !dropIsSame && !dropIsParent;
        return canDrop;
      },
      drop() {
        // Never drop on Cards but only DropZones
        return;
      },
      collect: (monitor) => ({
        isOverCurrent: monitor.isOver(),
        // isOverCurrent: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [section._id, section.realPath],
  );

  //----------------------------------------------------------------
  // 3. ref
  //----------------------------------------------------------------

  const ref = useRef<HTMLDivElement | null>(null);

  // TODO : check if we can refactor this code in order to respect this rule
  // eslint-disable-next-line react-hooks/refs
  dropRef(previewRef(ref));

  const setDragRef = useCallback(
    (node: HTMLDivElement | null) => {
      dragRef(node);
    },
    [dragRef],
  );

  //----------------------------------------------------------------
  // 4. render
  //----------------------------------------------------------------

  return (
    <>
      <DropZone
        isVisible={!!showTarget}
        targetSectionInsertionPointRealPath={section.insertionPoint?.realPath}
        targetSectionRealPath={section.realPath}
      />
      <Paper
        ref={ref}
        sx={{ padding: '0.5rem 1rem', opacity }}
        variant="outlined"
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{ dislpay: 'flex', alignItems: 'flex-end' }}
        >
          <Typography
            sx={{ pt: '8px', flexGrow: 1, color: 'text.secondary' }}
            variant="body1"
          >
            {section.isVirtual ? (
              section.title
            ) : (
              <>
                <Box component="span" sx={{ mr: matches ? 3 : 1 }}>
                  {pathToChapters(section.displayPath ?? EMPTY_ARRAY)}
                </Box>
                {section.title ?? EMPTY_STRING}
              </>
            )}
          </Typography>
          {isContentEditable ? (
            <Box ref={setDragRef} sx={{ cursor: 'move', display: 'flex' }}>
              <DragIndicatorIcon color="action" />
            </Box>
          ) : null}
        </Stack>
        {section.subSections.length ? (
          section.subSections.map((subSection, idx, subSections) => (
            <Section
              isLastChild={idx === subSections.length - 1}
              key={subSection._id}
              section={subSection}
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
              showTarget={!!(isOverCurrent && canDrop)}
            />
          ))
        ) : (
          <DropZone
            isVisible={(!isDragging && showTarget) ?? false}
            targetSectionInsertionPointRealPath={
              section.insertionPoint?.realPath
            }
            targetSectionRealPath={[...section.realPath, 0]}
          />
        )}
      </Paper>
      {!!isLastChild && (
        <DropZone
          isVisible={!!showTarget}
          targetSectionInsertionPointRealPath={section.insertionPoint?.realPath}
          targetSectionRealPath={nextPath(section.realPath)}
        />
      )}
    </>
  );
};
